import { create } from 'zustand';
import api from '../lib/api';
import type {
  SchoolProposal, MinicrmSummary, RunState, ContactsPage, MinicrmProposal,
  SchoolDecisionPayload, OrgSearchResult, ContactDecision,
} from '../types/minicrmMigration';

interface ContactFilter { action?: string; school?: string; blocked?: boolean; }

interface State {
  summary: MinicrmSummary | null;
  run: RunState | null;
  schools: SchoolProposal[];
  schoolsIncludeDecided: boolean;
  contacts: ContactsPage | null;
  isLoading: boolean;
  busy: boolean;
  error: string | null;

  fetchSummary: () => Promise<void>;
  fetchSchools: (includeDecided?: boolean) => Promise<void>;
  decideSchool: (id: string, payload: SchoolDecisionPayload) => Promise<void>;
  searchOrgs: (q: string) => Promise<OrgSearchResult[]>;
  fetchContacts: (filter: ContactFilter, page: number) => Promise<void>;
  decideContact: (id: string, decision: ContactDecision, proposedPersonId?: string) => Promise<void>;
  searchPersons: (q: string) => Promise<Array<{ id: string; firstName: string; lastName: string; email: string | null }>>;
  rescan: () => Promise<void>;
  commit: () => Promise<void>;
}

const BASE = '/api/admin/minicrm-migration';

export const useMinicrmMigrationStore = create<State>((set, get) => ({
  summary: null,
  run: null,
  schools: [],
  schoolsIncludeDecided: false,
  contacts: null,
  isLoading: false,
  busy: false,
  error: null,

  fetchSummary: async () => {
    try {
      set({ isLoading: true, error: null });
      const res = await api.get(BASE);
      set({ summary: res.data.summary, run: res.data.run, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: msg(err, 'Failed to load summary') });
    }
  },

  fetchSchools: async (includeDecided = false) => {
    try {
      set({ schoolsIncludeDecided: includeDecided });
      const res = await api.get(`${BASE}/schools${includeDecided ? '?includeDecided=true' : ''}`);
      set({ schools: res.data.schools });
    } catch (err) {
      set({ error: msg(err, 'Failed to load schools') });
    }
  },

  decideSchool: async (id, payload) => {
    try {
      await api.patch(`${BASE}/schools/${id}`, payload);
      // Re-fetch in the same mode so decided rows stay visible (the "agree / Change" view).
      await Promise.all([get().fetchSchools(get().schoolsIncludeDecided), get().fetchSummary()]);
    } catch (err) {
      set({ error: msg(err, 'Failed to record school decision') });
    }
  },

  searchOrgs: async (q) => {
    try {
      const res = await api.get(`${BASE}/orgs/search?q=${encodeURIComponent(q)}`);
      return res.data.orgs;
    } catch {
      return [];
    }
  },

  fetchContacts: async (filter, page) => {
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (filter.action) params.set('action', filter.action);
      if (filter.school) params.set('school', filter.school);
      if (filter.blocked) params.set('blocked', 'true');
      const res = await api.get(`${BASE}/contacts?${params.toString()}`);
      set({ contacts: res.data });
    } catch (err) {
      set({ error: msg(err, 'Failed to load contacts') });
    }
  },

  decideContact: async (id, decision, proposedPersonId) => {
    try {
      await api.patch(`${BASE}/contacts/${id}`, { decision, proposedPersonId });
    } catch (err) {
      set({ error: msg(err, 'Failed to update contact') });
    }
  },

  searchPersons: async (q) => {
    try {
      const res = await api.get(`${BASE}/persons/search?q=${encodeURIComponent(q)}`);
      return res.data.persons;
    } catch {
      return [];
    }
  },

  rescan: async () => { await runAndPoll(set, get, 'rescan', 'Failed to start re-scan'); },
  commit: async () => { await runAndPoll(set, get, 'commit', 'Failed to start commit'); },
}));

function msg(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message ?? fallback;
}

async function runAndPoll(
  set: (s: Partial<State>) => void,
  get: () => State,
  kind: 'rescan' | 'commit',
  failMsg: string,
): Promise<void> {
  try {
    set({ busy: true, error: null });
    await api.post(`${BASE}/${kind}`);
    const poll = async (): Promise<void> => {
      await get().fetchSummary();
      if (get().run?.running) {
        setTimeout(poll, 2000);
      } else {
        set({ busy: false });
        await get().fetchSchools(get().schoolsIncludeDecided);
      }
    };
    setTimeout(poll, 1000);
  } catch (err) {
    set({ busy: false, error: msg(err, failMsg) });
  }
}
